<template>
  <div class="nr1-analytics" id="nr1-report-content">
    <div class="d-flex justify-content-end align-items-center mb-3">
      <div class="d-print-none">
        <b-button variant="secondary" @click="$router.back()">
          <i class="fa fa-arrow-left"></i> Voltar
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-3">Consolidando dados do PGR...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      <i class="fa fa-exclamation-triangle"></i> {{ error }}
    </div>

    <div v-else>
      <b-card class="metrics-container mb-4">
        <div class="metrics-grid">
          <div
            v-for="metric in metricsCards"
            :key="metric.key"
            class="metric-section"
          >
            <div class="metric-header">
              <div class="metric-icon">
                <i :class="metric.icon"></i>
              </div>
              <p class="metric-label">{{ metric.label }}</p>
            </div>
            <h3 class="metric-value">{{ metric.value }}</h3>
            <div class="progress-container mt-2">
              <div
                class="progress-bar"
                :style="{ width: `${metricProgress(metric)}%` }"
              ></div>
            </div>
          </div>
        </div>
      </b-card>

      <b-row>
        <!-- Heatmap da Matriz de Risco -->
        <b-col lg="6">
          <b-card no-body class="dashboard-card chart-animation mb-4">
            <b-card-body>
              <h6 class="mb-0">Matriz de Calor (Severidade x Probabilidade)</h6>
              <div class="risk-matrix-container">
                <div class="y-axis-label">Severidade</div>
                <div class="matrix-grid">
                  <div
                    class="matrix-row"
                    v-for="s in [4, 3, 2, 1, 0]"
                    :key="'s' + s"
                  >
                    <div class="s-label">{{ s + 1 }}</div>
                    <div
                      v-for="p in [0, 1, 2, 3, 4]"
                      :key="'p' + p"
                      class="matrix-cell"
                      :style="{ backgroundColor: getCellColor(s, p) }"
                      @click="selectQuadrant(s, p)"
                      :class="{ 'has-data': heatMapMatrix[s][p].count > 0 }"
                    >
                      <span
                        v-if="heatMapMatrix[s][p].count > 0"
                        class="risk-count"
                      >
                        {{ heatMapMatrix[s][p].count }}
                      </span>
                    </div>
                  </div>
                  <div class="matrix-row labels-row">
                    <div class="s-label"></div>
                    <div
                      class="p-label"
                      v-for="option in [
                        { value: 1, label: 'Nunca' },
                        { value: 2, label: 'Raramente' },
                        { value: 3, label: 'Às vezes' },
                        { value: 4, label: 'Frequentemente' },
                        { value: 5, label: 'Sempre' },
                      ]"
                      :key="'pl' + option.value"
                    >
                      {{ option.label }}
                    </div>
                  </div>
                  <div class="x-axis-label text-center mt-2">Probabilidade</div>
                </div>
              </div>
              <div v-if="selectedQuadrantItems" class="mt-3 quadrant-details">
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <h6 class="mb-0 small font-weight-bold">
                    Itens Selecionados no Quadrante:
                  </h6>
                  <b-button
                    size="sm"
                    variant="link"
                    class="p-0"
                    @click="selectedQuadrantItems = null"
                    >Limpar</b-button
                  >
                </div>
                <div
                  class="list-group list-group-flush border rounded"
                  style="max-height: 200px; overflow-y: auto"
                >
                  <div
                    v-for="(item, idx) in selectedQuadrantItems"
                    :key="idx"
                    class="list-group-item small p-2"
                  >
                    <strong>{{ item.area }}</strong
                    >: {{ item.categoryName }} -
                    {{ item.questionText || "Pergunta" }} (Score:
                    {{ item.riskScore }})
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Radar de Categorias -->
        <b-col lg="6">
          <b-card no-body class="dashboard-card chart-animation mb-4">
            <b-card-body>
              <h6 class="mb-0">Radar de Riscos por Categoria</h6>
              <apexchart
                type="radar"
                height="350"
                :options="radarOptions"
                :series="radarSeries"
              ></apexchart>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <!-- Top 5 Setores -->
        <b-col lg="6">
          <b-card no-body class="dashboard-card chart-animation mb-4">
            <b-card-body>
              <h6 class="mb-0">Top 5 Setores Mais Críticos</h6>
              <apexchart
                type="bar"
                height="300"
                :options="topAreasOptions"
                :series="topAreasSeries"
              ></apexchart>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Evolução do Sentimento -->
        <b-col lg="6">
          <b-card no-body class="dashboard-card chart-animation mb-4">
            <b-card-body>
              <h6 class="mb-0">Evolução do Sentimento de Segurança</h6>
              <apexchart
                type="line"
                height="300"
                :options="evolutionOptions"
                :series="evolutionSeries"
              ></apexchart>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <b-card no-body class="dashboard-card chart-animation mb-4">
            <b-card-body>
              <h6 class="mb-3">Top 8 Perguntas Críticas</h6>
              <b-table
                :items="topQuestionRisks"
                :fields="questionFields"
                hover
                small
                responsive
                class="nr1-table mb-0"
              >
                <template #cell(riskLevel)="data">
                  <b-badge :variant="getRiskVariant(data.value)">
                    {{ data.value }}
                  </b-badge>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col lg="6">
          <b-card no-body class="dashboard-card chart-animation mb-4">
            <b-card-body>
              <h6 class="mb-3">Top 8 Categorias Críticas</h6>
              <b-table
                :items="topCategoryRisks"
                :fields="topCategoryFields"
                hover
                small
                responsive
                class="nr1-table mb-0"
              >
                <template #cell(riskLevel)="data">
                  <b-badge :variant="getRiskVariant(data.value)">
                    {{ data.value }}
                  </b-badge>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Tabelas Detalhadas -->
      <b-tabs content-class="mt-3">
        <b-tab title="Perigos Identificados" active>
          <b-table
            :items="analytics.areaRisks"
            :fields="areaFields"
            hover
            responsive
            class="nr1-table"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
            <template #cell(area)="data">
              <strong>{{ data.value }}</strong>
              <br /><small class="text-muted"
                >{{ data.item.totalResponses }} respostas</small
              >
            </template>
            <template #cell(overallRiskScore)="data">
              <b-badge
                :variant="getRiskVariant(data.item.overallRiskLevel)"
                class="px-3 py-2"
              >
                {{ data.value.toFixed(1) }}
              </b-badge>
            </template>
            <template #cell(overallRiskLevel)="data">
              <span
                class="font-weight-bold"
                :class="'text-' + getRiskVariant(data.value)"
                >{{ data.value }}</span
              >
            </template>
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="outline-info"
                @click="viewAreaDetails(data.item)"
              >
                <i class="fa fa-list"></i> Detalhes
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <b-tab title="Planos de Ação (5W2H)">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Cronograma de Medidas Preventivas e Corretivas</h6>
            <div class="d-flex align-items-center">
              <b-button
                v-if="selectedActionPlanIds.length"
                variant="success"
                size="sm"
                class="mr-2"
                @click="markSelectedActionPlansAsCompleted"
              >
                <i class="fa fa-check"></i> Marcar como concluído
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                @click="openActionPlanModal(null)"
              >
                <i class="fa fa-plus"></i> Novo Plano
              </b-button>
            </div>
          </div>
          <b-table
            :items="actionPlans"
            :fields="actionPlanFields"
            hover
            responsive
            class="nr1-table"
          >
            <template #cell(select)="data">
              <b-form-checkbox
                :checked="isActionPlanSelected(data.item._id)"
                @change="toggleActionPlanSelection(data.item._id, $event)"
              ></b-form-checkbox>
            </template>
            <template #cell(when)="data">{{ formatDate(data.value) }}</template>
            <template #cell(status)="data">
              <b-badge
                :variant="
                  data.value === 'Concluído'
                    ? 'success'
                    : data.value === 'Pendente'
                    ? 'warning'
                    : 'info'
                "
              >
                {{ data.value }}
              </b-badge>
            </template>
            <template #cell(item_actions)="data">
              <b-button-group size="sm">
                <b-button
                  variant="link"
                  class="text-info p-0 mr-2"
                  @click="openActionPlanModal(data.item)"
                >
                  <i class="fa fa-edit"></i>
                </b-button>
                <b-button
                  variant="link"
                  class="text-danger p-0"
                  @click="removeActionPlan(data.item._id)"
                >
                  <i class="fa fa-trash"></i>
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </div>

    <b-modal
      v-model="showAreaDetailsModal"
      title="Detalhes da Área"
      size="xl"
      hide-footer
      @hidden="selectedArea = null"
    >
      <div v-if="selectedArea">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 class="mb-1">{{ selectedArea.area }}</h5>
            <small class="text-muted"
              >{{ selectedArea.totalResponses || 0 }} respostas</small
            >
          </div>
          <div class="text-right">
            <div class="mb-1">
              <small class="text-muted mr-2">Score médio:</small>
              <b-badge :variant="getRiskVariant(selectedArea.overallRiskLevel)">
                {{ Number(selectedArea.overallRiskScore || 0).toFixed(1) }}
              </b-badge>
            </div>
            <div>
              <small class="text-muted mr-2">Nível de risco:</small>
              <span
                class="font-weight-bold"
                :class="'text-' + getRiskVariant(selectedArea.overallRiskLevel)"
              >
                {{ selectedArea.overallRiskLevel || "Não informado" }}
              </span>
            </div>
          </div>
        </div>

        <b-table
          :items="Array.isArray(selectedArea.risks) ? selectedArea.risks : []"
          :fields="categoryFields"
          hover
          class="nr1-table"
          empty-text="Nenhum detalhe de risco encontrado para esta área."
        >
          <template #cell(category)="data"
            ><strong>{{ data.item.categoryName }}</strong></template
          >
          <template #cell(avgProbability)="data">
            <b-progress :value="(data.value / 5) * 100" height="15px">
              <b-progress-bar
                :value="(data.value / 5) * 100"
                :variant="getResponseVariant(data.value)"
              >
                {{ data.value.toFixed(1) }} / 5
              </b-progress-bar>
            </b-progress>
          </template>
          <template #cell(riskScore)="data">
            <b-badge :variant="getRiskVariant(data.item.riskLevel)">{{
              data.value.toFixed(1)
            }}</b-badge>
          </template>
          <template #cell(action)="data">
            <b-button
              size="sm"
              variant="link"
              @click="
                openActionPlanModal(
                  null,
                  selectedArea.area,
                  data.item.categoryId
                )
              "
            >
              <i class="fa fa-plus-circle"></i> Planejar
            </b-button>
          </template>
        </b-table>
      </div>
      <b-alert v-else variant="info" show>
        Nenhum dado da área foi selecionado.
      </b-alert>
    </b-modal>

    <!-- Modal 5W2H -->
    <b-modal
      v-model="showActionPlanModal"
      title="Plano de Ação 5W2H"
      @ok="handleActionPlanOk"
      size="lg"
      ok-title="Salvar"
      cancel-title="Voltar"
    >
      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group label="Área (Obrigatório)">
              <b-form-input
                v-model="newActionPlan.area"
                placeholder="Ex: Produção"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Categoria de risco (Obrigatório)">
              <b-form-select
                v-model="newActionPlan.categoryId"
                :options="actionPlanCategoryOptions"
              ></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="O que (What) - Medida Corretiva/Preventiva">
              <b-form-input
                v-model="newActionPlan.what"
                placeholder="Ex: Treinamento em NR-10"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Por que (Why) - Justificativa">
              <b-form-textarea
                v-model="newActionPlan.why"
                rows="2"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Como (How) - Método de execução">
              <b-form-textarea
                v-model="newActionPlan.how"
                rows="2"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Onde (Where) - Setor">
              <b-form-input v-model="newActionPlan.where"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Quem (Who) - Responsável">
              <b-form-input v-model="newActionPlan.who"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Quando (When) - Prazo">
              <b-form-datepicker
                v-model="newActionPlan.when"
                locale="pt-BR"
                :date-format-options="{
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }"
                :value-as-date="false"
                placeholder="dd/mm/aaaa"
              ></b-form-datepicker>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Quanto (How Much) - Custo">
              <b-form-input
                v-model="newActionPlan.howMuch"
                placeholder="R$ 0,00"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Status">
              <b-select
                v-model="newActionPlan.status"
                :options="[
                  'Pendente',
                  'Em Andamento',
                  'Concluído',
                  'Cancelado',
                ]"
              ></b-select>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>

    <!-- Alerta de Privacidade Reutilizado -->
    <b-alert
      v-if="analytics.areaThresholdFallbackApplied"
      variant="info"
      show
      class="mt-3"
    >
      <i class="fa fa-info-circle mr-2"></i>
      Alguns setores não atingiram o mínimo configurado de respostas. Exibimos
      os dados disponíveis para evitar gráfico vazio.
    </b-alert>

    <b-alert
      v-else-if="analytics.insufficientData"
      variant="info"
      show
      class="mt-3"
    >
      <i class="fa fa-shield-alt mr-2"></i>
      <strong>Aviso de Proteção de Dados:</strong> Algumas áreas aparecem
      ocultas por não atingirem o mínimo de feedbacks necessário para garantir o
      anonimato dos trabalhadores.
    </b-alert>
  </div>
</template>

<script>
import {
  getQuestionnaire,
  getAnalytics,
  getDashboardStats,
  getRiskEvolution,
  getActionPlans,
  createActionPlan,
  updateActionPlan,
  deleteActionPlan,
} from "@/api/nr1";
import VueApexCharts from "vue-apexcharts";
import moment from "moment";

export default {
  name: "NR1Analytics",
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      questionnaire: {},
      analytics: {},
      stats: {
        criticalityIndex: 0,
        criticalityLevel: "Baixo",
        adoptionRate: 0,
        criticalAlerts: 0,
        overdueActionPlans: 0,
      },
      evolutionData: [],
      actionPlans: [],
      selectedActionPlanIds: [],
      selectedArea: null,
      selectedQuadrantItems: null,
      showAreaDetailsModal: false,
      showActionPlanModal: false,
      editingActionPlan: null,
      newActionPlan: {
        what: "",
        why: "",
        where: "",
        when: "",
        who: "",
        how: "",
        howMuch: "",
        status: "Pendente",
        area: "",
        categoryId: "",
      },
      sortBy: "overallRiskScore",
      sortDesc: true,
      areaFields: [
        { key: "area", label: "Área", sortable: true },
        {
          key: "totalResponses",
          label: "Respostas",
          sortable: true,
          class: "text-center",
        },
        {
          key: "overallRiskScore",
          label: "Score Médio",
          sortable: true,
          class: "text-center",
        },
        {
          key: "overallRiskLevel",
          label: "Nível de Risco",
          sortable: true,
          class: "text-center",
        },
        { key: "actions", label: "Ações", class: "text-center" },
      ],
      categoryFields: [
        { key: "category", label: "Categoria" },
        { key: "avgProbability", label: "Resposta Média (Probabilidade)" },
        { key: "severity", label: "Gravidade", class: "text-center" },
        { key: "riskScore", label: "Score de Risco", class: "text-center" },
        { key: "riskLevel", label: "Nível", class: "text-center" },
        { key: "action", label: "Ação", class: "text-center" },
      ],
      questionFields: [
        { key: "questionText", label: "Pergunta" },
        { key: "categoryName", label: "Categoria" },
        { key: "riskScore", label: "Score", class: "text-center" },
        { key: "riskLevel", label: "Nível", class: "text-center" },
      ],
      topCategoryFields: [
        { key: "categoryName", label: "Categoria" },
        { key: "riskScore", label: "Score", class: "text-center" },
        { key: "riskLevel", label: "Nível", class: "text-center" },
      ],
      actionPlanFields: [
        {
          key: "select",
          label: "",
          class: "text-center",
          thStyle: { width: "42px" },
          tdClass: "px-1 text-center",
        },
        { key: "what", label: "O que" },
        { key: "area", label: "Onde (Setor)" },
        { key: "who", label: "Quem" },
        { key: "howMuch", label: "Quanto" },
        { key: "when", label: "Quando", sortable: true },
        { key: "status", label: "Status" },
        { key: "item_actions", label: "Ações" },
      ],
      heatMapMatrix: Array(5)
        .fill()
        .map(() => Array(5).fill(0)),
    };
  },
  computed: {
    actionPlanCategoryOptions() {
      const categories = Array.isArray(this.questionnaire.categories)
        ? this.questionnaire.categories
        : [];

      return [
        { value: "", text: "Selecione a categoria" },
        ...categories.map((category) => ({
          value: String(category.id),
          text: category.name,
        })),
      ];
    },
    topQuestionRisks() {
      return this.analytics.questionRisks
        ? this.analytics.questionRisks.slice(0, 8)
        : [];
    },
    topCategoryRisks() {
      return this.analytics.categoryRisks
        ? this.analytics.categoryRisks.slice(0, 8)
        : [];
    },
    metricsCards() {
      return [
        {
          key: "criticality",
          label: "Índice de criticidade",
          icon: "fas fa-chart-line",
          value: this.stats.criticalityIndex,
        },
        {
          key: "adoption",
          label: "Aderência",
          icon: "fas fa-check-circle",
          value: `${this.stats.adoptionRate}%`,
        },
        {
          key: "criticalAlerts",
          label: "Alertas críticos",
          icon: "fas fa-bell",
          value: this.stats.criticalAlerts,
        },
        {
          key: "overduePlans",
          label: "Planos atrasados",
          icon: "fas fa-clock",
          value: this.stats.overdueActionPlans,
        },
        {
          key: "responses",
          label: "Respostas",
          icon: "fas fa-reply",
          value: this.analytics.totalResponses || 0,
        },
        {
          key: "questionnaires",
          label: "Questionários",
          icon: "fas fa-file-alt",
          value: 1,
        },
      ];
    },
    radarOptions() {
      return {
        chart: { type: "radar", toolbar: { show: false } },
        xaxis: {
          categories: this.analytics.categoryRisks
            ? this.analytics.categoryRisks.map((c) => c.categoryName)
            : [],
        },
        yaxis: { max: 25 },
        stroke: { width: 2 },
        fill: { opacity: 0.4 },
        colors: ["#00E396"],
      };
    },
    radarSeries() {
      return [
        {
          name: "Score de Risco",
          data: this.analytics.categoryRisks
            ? this.analytics.categoryRisks.map((c) => c.riskScore)
            : [],
        },
      ];
    },
    topAreasOptions() {
      return {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, distributed: true } },
        xaxis: {
          categories: this.analytics.areaRisks
            ? this.analytics.areaRisks.slice(0, 5).map((a) => a.area)
            : [],
        },
        colors: ["#d32f2f", "#f57c00", "#fbc02d", "#757575", "#388e3c"],
        legend: { show: false },
      };
    },
    topAreasSeries() {
      return [
        {
          name: "Score de Risco",
          data: this.analytics.areaRisks
            ? this.analytics.areaRisks
                .slice(0, 5)
                .map((a) => a.overallRiskScore)
            : [],
        },
      ];
    },
    evolutionOptions() {
      return {
        chart: { type: "line", toolbar: { show: false } },
        xaxis: { categories: this.evolutionData.map((e) => e.date) },
        stroke: { curve: "smooth" },
        markers: { size: 4 },
      };
    },
    evolutionSeries() {
      return [
        {
          name: "Score Médio",
          data: this.evolutionData.map((e) => e.score),
        },
      ];
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    handleActionPlanOk(event) {
      event.preventDefault();
      this.saveActionPlan();
    },
    isActionPlanSelected(actionPlanId) {
      return this.selectedActionPlanIds.includes(String(actionPlanId));
    },
    toggleActionPlanSelection(actionPlanId, selected) {
      const normalizedId = String(actionPlanId);
      if (selected) {
        if (!this.isActionPlanSelected(normalizedId)) {
          this.selectedActionPlanIds.push(normalizedId);
        }
        return;
      }

      this.selectedActionPlanIds = this.selectedActionPlanIds.filter(
        (id) => id !== normalizedId
      );
    },
    async loadActionPlans() {
      const response = await getActionPlans(this.id);
      this.actionPlans = response.data;

      const availableIds = new Set(
        this.actionPlans.map((actionPlan) => String(actionPlan._id))
      );
      this.selectedActionPlanIds = this.selectedActionPlanIds.filter((id) =>
        availableIds.has(id)
      );
    },
    validateActionPlan() {
      const validators = [
        {
          valid: !!String(this.newActionPlan.area || "").trim(),
          message: "Informe a área do plano de ação.",
        },
        {
          valid: !!String(this.newActionPlan.categoryId || "").trim(),
          message: "Selecione a categoria de risco.",
        },
        {
          valid: !!String(this.newActionPlan.what || "").trim(),
          message: "Informe o que será feito.",
        },
        {
          valid: !!this.newActionPlan.when,
          message: "Informe o prazo do plano de ação.",
        },
        {
          valid: !!String(this.newActionPlan.who || "").trim(),
          message: "Informe o responsável pelo plano.",
        },
      ];

      const invalidField = validators.find((item) => !item.valid);
      if (invalidField) {
        this.$bvToast.toast(invalidField.message, {
          title: "Validação",
          variant: "warning",
          solid: true,
        });
        return false;
      }

      return true;
    },
    async loadData() {
      try {
        this.loading = true;
        this.error = null;

        const [qRes, aRes, sRes, eRes, apRes] = await Promise.all([
          getQuestionnaire(this.id),
          getAnalytics(this.id),
          getDashboardStats(this.id),
          getRiskEvolution(this.id),
          getActionPlans(this.id),
        ]);

        this.questionnaire = qRes.data;
        this.analytics = aRes.data;
        this.stats = sRes.data;
        this.evolutionData = eRes.data;
        this.actionPlans = apRes.data;
        this.selectedActionPlanIds = [];

        this.buildHeatMap();
      } catch (err) {
        console.error("Erro ao carregar dados:", err);
        this.error = "Não foi possível carregar os dados do dashboard.";
      } finally {
        this.loading = false;
      }
    },
    buildHeatMap() {
      const matrix = Array(5)
        .fill()
        .map(() => Array(5).fill({ count: 0, items: [] }));
      if (this.analytics.matrix) {
        this.analytics.matrix.forEach((cell) => {
          // Severidade e Probabilidade são 1-5
          // Arrays 0-indexados: S (linhas) 0-4, P (colunas) 0-4
          const sIdx = cell.severity - 1;
          const pIdx = cell.probability - 1;
          if (sIdx >= 0 && sIdx < 5 && pIdx >= 0 && pIdx < 5) {
            matrix[sIdx][pIdx] = {
              count: cell.count,
              items: cell.items || [],
            };
          }
        });
      }
      this.heatMapMatrix = matrix;
    },
    getCellColor(s, p) {
      const score = (s + 1) * (p + 1);
      if (score >= 16) return "#ffcdd2"; // Red
      if (score >= 11) return "#ffe0b2"; // Orange
      if (score >= 6) return "#fff9c4"; // Yellow
      return "#c8e6c9"; // Green
    },
    selectQuadrant(s, p) {
      this.selectedQuadrantItems = this.heatMapMatrix[s][p].items;
    },
    viewAreaDetails(item) {
      this.selectedArea = item;
      this.showAreaDetailsModal = true;
    },
    openActionPlanModal(item, area = null, categoryId = null) {
      this.editingActionPlan = item;
      if (item) {
        this.newActionPlan = {
          ...item,
          when: moment(item.when).format("YYYY-MM-DD"),
          categoryId: String(item.categoryId || ""),
        };
      } else {
        this.newActionPlan = {
          what: "",
          why: "",
          where: area || "",
          when: "",
          who: "",
          how: "",
          howMuch: "",
          status: "Pendente",
          area: area || "",
          categoryId: categoryId ? String(categoryId) : "",
          questionnaire: this.id,
        };
      }
      this.showActionPlanModal = true;
    },
    async saveActionPlan() {
      if (!this.validateActionPlan()) {
        return;
      }

      const payload = {
        ...this.newActionPlan,
        questionnaire: Number(this.id),
        categoryId: String(this.newActionPlan.categoryId),
      };

      try {
        if (this.editingActionPlan) {
          await updateActionPlan(this.editingActionPlan._id, payload);
        } else {
          await createActionPlan(payload);
        }
        this.showActionPlanModal = false;
        await this.loadActionPlans();
        this.$bvToast.toast("Plano de ação salvo com sucesso.", {
          title: "Sucesso",
          variant: "success",
          solid: true,
        });
      } catch (err) {
        console.error("Erro ao salvar plano:", err);
        const errorMessage =
          err?.response?.data?.message ||
          "Não foi possível salvar o plano de ação.";

        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }
    },
    async removeActionPlan(id) {
      if (confirm("Deseja remover este plano de ação?")) {
        await deleteActionPlan(id);
        await this.loadActionPlans();
      }
    },
    async markSelectedActionPlansAsCompleted() {
      if (!this.selectedActionPlanIds.length) {
        return;
      }

      if (!confirm("Marcar os planos selecionados como concluídos?")) {
        return;
      }

      try {
        await Promise.all(
          this.selectedActionPlanIds.map((id) =>
            updateActionPlan(id, {
              status: "Concluído",
            })
          )
        );

        this.selectedActionPlanIds = [];
        await this.loadActionPlans();

        this.$bvToast.toast("Planos selecionados concluídos com sucesso.", {
          title: "Sucesso",
          variant: "success",
          solid: true,
        });
      } catch (err) {
        console.error("Erro ao concluir planos selecionados:", err);
        const errorMessage =
          err?.response?.data?.message ||
          "Não foi possível concluir os planos selecionados.";

        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }
    },
    formatDate(date) {
      if (!date) {
        return "-";
      }

      return moment.utc(date).format("DD/MM/YYYY");
    },
    getRiskVariant(level) {
      const variants = {
        Crítico: "danger",
        Alto: "warning",
        Médio: "info",
        Baixo: "success",
      };
      return variants[level] || "secondary";
    },
    metricProgress(metric) {
      const numericValue = parseFloat(String(metric.value).replace("%", ""));
      if (Number.isNaN(numericValue) || numericValue <= 0) {
        return 0;
      }

      if (String(metric.value).includes("%")) {
        return Math.min(numericValue, 100);
      }

      const values = this.metricsCards
        .map((item) => parseFloat(String(item.value).replace("%", "")))
        .filter((value) => !Number.isNaN(value));
      const maxValue = Math.max(...values, 1);

      return Math.min((numericValue / maxValue) * 100, 100);
    },
    getResponseVariant(value) {
      if (value >= 4) return "danger";
      if (value >= 3) return "warning";
      if (value >= 2) return "info";
      return "success";
    },
    getCategoryName(categoryId) {
      const category = this.questionnaire.categories
        ? this.questionnaire.categories.find((c) => c.id === categoryId)
        : null;
      return category ? category.name : categoryId;
    },
  },
};
</script>

<style scoped>
.nr1-analytics {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.risk-matrix-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.y-axis-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-weight: bold;
  margin-right: 15px;
  color: #666;
}

.matrix-grid {
  display: flex;
  flex-direction: column;
}

.matrix-row {
  display: flex;
  flex-direction: row;
}

.matrix-cell {
  width: 60px;
  height: 60px;
  border: 1px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.matrix-cell:hover {
  filter: brightness(0.9);
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.matrix-cell.has-data {
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.1);
}

.risk-count {
  font-weight: bold;
  font-size: 1.2rem;
  color: #333;
}

.s-label,
.p-label {
  width: 60px;
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #888;
  font-size: 0.62rem;
  line-height: 1.1;
  text-align: center;
  padding: 0.15rem;
}

.s-label {
  width: 30px;
  height: 60px;
  margin-right: 10px;
}

.labels-row {
  margin-top: 10px;
}

.blink {
  animation: blinker 1.5s linear infinite;
}

@keyframes blinker {
  50% {
    opacity: 0.3;
  }
}

.metrics-container {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}

.metrics-grid {
  display: flex;
  align-items: stretch;
  min-height: 110px;
}

.metric-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.25rem 1rem;
}

.metric-section:not(:last-child) {
  border-right: 1px dashed #cbd5e0;
}

.metric-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin: 0 auto 0.45rem auto;
}

.metric-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #000000;
}

.metric-label {
  margin: 0;
  font-size: 0.82rem;
  color: #000000;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.2;
  white-space: nowrap;
}

.metric-value {
  font-size: 2rem;
  font-weight: 600;
  margin: 0;
  color: #000000;
  line-height: 1;
}

.progress-container {
  width: 75%;
  height: 5px;
  background-color: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
}

.progress-bar {
  height: 100%;
  background: #4299e1;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.dashboard-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}

.nr1-table ::v-deep thead th {
  color: #718096;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  border-top: none;
}

.nr1-table ::v-deep tbody td {
  vertical-align: middle;
}

.badge {
  font-size: 0.85rem;
  font-weight: 600;
}

@media (max-width: 991px) {
  .metrics-grid {
    flex-direction: column;
  }

  .metric-section:not(:last-child) {
    border-right: none;
    border-bottom: 1px dashed #cbd5e0;
  }
}

@media print {
  .d-print-none {
    display: none !important;
  }
  .card {
    border: none !important;
    box-shadow: none !important;
  }
}
</style>
